import { Box, Spinner } from '@cardboard-ui/react';
import { DIRECTION, isTreeScrollable } from 'components/PullToRefresh/utils';
import React, { useEffect, useRef } from 'react';
import { Layout, useActiveLayout } from 'utils/LayoutProvider';
import { useFeatureFlag } from 'utils/sessionProvider';

interface PullToRefreshProps {
  isRefreshing: boolean;
  onRefresh: () => void;
  children: JSX.Element;
  pullDownThreshold?: number;
  maxPullDownDistance?: number;
  resistance?: number;
}

export const PullToRefresh: React.FC<PullToRefreshProps> = ({
  isRefreshing,
  onRefresh,
  children,
  pullDownThreshold = 67,
  maxPullDownDistance = 80,
  resistance = 1,
}) => {
  const isPullToRefreshEnabled = useFeatureFlag('FEATURE_PULL_TO_REFRESH');

  const isMobile = useActiveLayout() !== Layout.DESKTOP;
  const containerRef = useRef<HTMLDivElement>(null);
  const childrenRef = useRef<HTMLDivElement>(null);
  const pullDownRef = useRef<HTMLDivElement>(null);

  let pullToRefreshThresholdBreached: boolean = false;

  let isDragging: boolean = false;
  let startY: number = 0;
  let currentY: number = 0;

  useEffect(() => {
    if (!isRefreshing) {
      initContainer();
    }
  }, [isRefreshing]);

  useEffect(() => {
    if (!childrenRef || !childrenRef.current) return;
    const childrenEl = childrenRef.current;

    childrenEl.addEventListener('touchstart', onTouchStart, { passive: true });
    childrenEl.addEventListener('mousedown', onTouchStart);
    childrenEl.addEventListener('touchmove', onTouchMove, { passive: false });
    childrenEl.addEventListener('mousemove', onTouchMove);
    childrenEl.addEventListener('touchend', onEnd);
    childrenEl.addEventListener('mouseup', onEnd);

    document.body.addEventListener('mouseleave', onEnd);

    return () => {
      childrenEl.removeEventListener('touchstart', onTouchStart);
      childrenEl.removeEventListener('mousedown', onTouchStart);
      childrenEl.removeEventListener('touchmove', onTouchMove);
      childrenEl.removeEventListener('mousemove', onTouchMove);
      childrenEl.removeEventListener('touchend', onEnd);
      childrenEl.removeEventListener('mouseup', onEnd);

      document.body.removeEventListener('mouseleave', onEnd);
    };
  }, [children, onRefresh, pullDownThreshold, maxPullDownDistance]);

  const initContainer = (): void => {
    requestAnimationFrame(() => {
      if (childrenRef.current) {
        childrenRef.current.style.overflowX = 'hidden';
        childrenRef.current.style.overflowY = 'auto';
        childrenRef.current.style.transform = `unset`;
      }
      if (pullDownRef.current) {
        pullDownRef.current.style.opacity = '0';
      }

      if (pullToRefreshThresholdBreached)
        pullToRefreshThresholdBreached = false;
    });
  };

  const onTouchStart = (e: MouseEvent | TouchEvent): void => {
    isDragging = false;
    if (e instanceof MouseEvent) {
      startY = e.pageY;
    }
    if (window.TouchEvent && e instanceof TouchEvent) {
      startY = e.touches[0].pageY;
    }
    currentY = startY;
    if (
      e.type === 'touchstart' &&
      isTreeScrollable(e.target as HTMLElement, DIRECTION.UP)
    ) {
      return;
    }
    if (childrenRef.current!.getBoundingClientRect().top < 0) {
      return;
    }
    isDragging = true;
  };

  const onTouchMove = (e: MouseEvent | TouchEvent): void => {
    if (!isDragging) {
      return;
    }

    if (window.TouchEvent && e instanceof TouchEvent) {
      currentY = e.touches[0].pageY;
    } else {
      currentY = (e as MouseEvent).pageY;
    }

    if (currentY < startY) {
      isDragging = false;
      return;
    }

    if (e.cancelable) {
      e.preventDefault();
    }

    const yDistanceMoved = Math.min(
      (currentY - startY) / resistance,
      maxPullDownDistance,
    );

    if (yDistanceMoved >= pullDownThreshold) {
      isDragging = true;
      pullToRefreshThresholdBreached = true;
    }

    if (yDistanceMoved >= maxPullDownDistance) {
      return;
    }
    pullDownRef.current!.style.opacity = (yDistanceMoved / 65).toString();
    pullDownRef.current!.style.visibility = 'visible';
    childrenRef.current!.style.transform = `translate(0px, ${yDistanceMoved}px)`;
    childrenRef.current!.style.overflow = 'visible';
  };

  const onEnd = (): void => {
    isDragging = false;
    startY = 0;
    currentY = 0;

    if (!pullToRefreshThresholdBreached) {
      if (pullDownRef.current) pullDownRef.current.style.visibility = 'hidden';
      initContainer();
      return;
    }

    if (childrenRef.current) {
      childrenRef.current.style.overflow = 'visible';
      childrenRef.current.style.transform = `translate(0px, ${pullDownThreshold}px)`;
    }

    onRefresh();
  };

  if (!isMobile || !isPullToRefreshEnabled) {
    return <>{children}</>;
  }

  return (
    <Box
      ref={containerRef}
      h="full"
      w="full"
      overflow="hidden"
      position="relative"
      style={{
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <Box
        ref={pullDownRef}
        position="absolute"
        overflow="hidden"
        left="0"
        right="0"
        top="0"
        zIndex="1"
        visibility="hidden"
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height={pullDownThreshold + 'px'}
        >
          <Spinner />
        </Box>
      </Box>

      <Box
        ref={childrenRef}
        h="full"
        w="full"
        overflow="hidden"
        position="relative"
        style={{
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
